const ml_app_builder = require('../../legacy/machinelogic_app_builder');
const { messageBox } = require('../../legacy/widgets');
const { machineAppInterface } = require("./machine_app_interface_singleton");
const { applyInjectedResources, resolveInjectionPoint } = require("../injectable_behavior_resolver");
class MachineAppImplementationLoader {
  constructor() {
    this.mImplementationManager = undefined;
    this.mOfficialRepo = undefined;
    this.mRepository = undefined;
  }

  load(pImplementationManager, pRepository, pCallback) {
    this.mImplementationManager = pImplementationManager;
    this.mOfficialRepo = pRepository;
    this.loadLibrary(pCallback);
  }

  _shouldUseLocalBundle() {
    return localStorage.getItem('machine_app_local_bundle') != null;
  }

  loadLibrary(pOnLoad) {
    this.mRepository = this.mOfficialRepo; // Always back up to the official repo

    if (!this._shouldUseLocalBundle()) {
      if (pOnLoad) pOnLoad();
      return;
    }

    const tryDynamicImport  = () => {
      // See details about the webpackIgnore here: https://stackoverflow.com/questions/56691391/dynamic-loading-of-external-modules-in-webpack-fails
      // See details about the Date.now() here: https://ar.al/2021/02/22/cache-busting-in-node.js-dynamic-esm-imports/
      import(/*webpackIgnore: true*/ `http://localhost:8080/bundle.js?update=${Date.now()}`).then(() => {
        this.mRepository = window.machineAppsComponents;
        messageBox('MachineApps has been dynamically reloaded.', 'success');
        pOnLoad && pOnLoad();
      }).catch(e => {
        messageBox('MachineApps failed to dynamically reload... retrying in 5 seconds', 'error');
        setTimeout(() => tryDynamicImport(), 5000);
      });
    };
    tryDynamicImport();
  }

  _isLoaded() {
    return this.mImplementationManager !== undefined && this.mRepository !== undefined;
  }

  /**
   * Loads the MachineApp implementation for a given type.
   * @param {string} pType Type of MachineApp
   * @returns {Record<'prerequisites' | 'configurator' | 'calibrator' | 'generator' | 'buildOperatorUi' | 'globalAssetCreatorMap' | 'validators' | 'optionalUiHooks' | 'admin' | 'getInjectableBehaviorPoints' | 'versioning', any>}
   */
  _loadMachineAppImplementationModule(pType) {
    if (!this._isLoaded()) {
      console.error('You must initialize MachineApps before using it.');
      throw new Error('You must initialize MachineApps before using it.');
    }

    const lMachineAppDefinition = this.mImplementationManager.getMachineAppDefinitionByType(pType);
    if (!lMachineAppDefinition) {
      console.error('Cannot show the configuration for MachineApp with type=' + pType);
      throw new Error('Cannot show the configuration for MachineApp with type=' + pType);
    }

    const lRepository = this.mRepository; // TODO: Get this on a per-machine app basis
    const lMachineApp = lMachineAppDefinition.getModule(lRepository);
    lMachineApp.preLoadHook && lMachineApp.preLoadHook(machineAppInterface.get());

    return lMachineApp;
  }

  /**
   * Returns true if the MachineApp type is reachable, otherwise false.
   * @param {string} pType The type of MachineApp that you want to load
   * @returns {boolean}
   */
  hasMachineAppType(pType) {
    return this.mImplementationManager.getMachineAppDefinitionByType(pType) !== undefined;
  }

  /**
   * Required MachineLogic config items to be able to access pType of MachineApp
   * @param {string} pType Type of MachineApp prerequisites that you want to see
   *
   * */
  getPrerequisites(pType) {
    return this._loadMachineAppImplementationModule(pType).prerequisites || {};
  }

  /**
   * @typedef {Object} CreatorProps
   * @property {Object | undefined} data Existing configuration.
   * @property {String} type Type of MachineApp configurator that you want to see
   * @property {(pStepDisplayList: any, pPassedParent: any) => void} updateStepper Function for updating the stepper in the left pane
   * @property {(configuration: Object) => void} setDirty Function to set the configurationa as dirty
   * @property {() => void} save Function to save the configuration immediately
   * @property {() => void} close Function to close the configurator
   */

  /**
   * Creates the configurator of the provided type. This configurator will be rendered by the caller.
   *
   * @param {CreatorProps}
   *
   * @returns {JQuery} Rendered module
   */
  createConfigurator({ data, type, updateStepper, setDirty, save, close }) {
    const lConfigurator = this._loadMachineAppImplementationModule(type).configurator;
    const lConfiguratorInterface = {
      machineAppInterface : machineAppInterface.get(),
      savedConfiguration  : data,
      updateStepper       : updateStepper,
      setDirty            : setDirty,
      save                : save,
      close               : close,
    };

    return lConfigurator(lConfiguratorInterface);
  }

  /**
   * @typedef {Object} MachineParametersProperties
   * @property {number} defaultStep
   * @typedef {CreatorProps & MachineParametersProperties} MachineParametersProps
   */

  /**
   * Creates the calibrator of the provided type. The calibrator will be rendered by the caller.
   *
   * @param {MachineParametersProps}
   *
   * @returns {JQuery} Rendered module
   */
  createMachineParameters({ data, type, updateStepper, setDirty, save, close, defaultStep }) {
    const lCalibrator = this._loadMachineAppImplementationModule(type).calibrator;
    const lCalibratorInterface = {
      machineAppInterface : machineAppInterface.get(),
      savedCalibration    : data,
      setDirty            : setDirty,
      save                : save,
      close               : close,
      updateStepper       : updateStepper,
      defaultStep         : defaultStep,
    };

    return lCalibrator(lCalibratorInterface);
  }

  /**
   * @typedef {Object} GlobalAssetsProperties
   * @property {string} assetType
   * @typedef {CreatorProps & GlobalAssetsProperties} GlobalAssetsProps
   */

  /**
   * Creates the globalAsset of the provided type. This globalAsset will be rendered by the caller. If globalAsset does not exist, it returns undefined
   *
   * @param {GlobalAssetsProps}
   *
   * @returns {Promise<JQuery>} Rendered module
   */
  createGlobalAsset({ data, type, updateStepper, globalAsset, setDirty, save, close, assetType }) {
    const creatorMap = this.getGlobalAssetCreatorMap(type);

    const globalAssetInterface = {
      machineAppInterface : machineAppInterface.get(),
      savedGlobalAsset    : data,
      globalAsset         : globalAsset,
      setDirty            : setDirty,
      save                : save,
      close               : close,
      updateStepper       : updateStepper,
    };

    return creatorMap[assetType](globalAssetInterface);
  }

  /**
   * Creates the operatorUi of the provided type. The operatorUi will be rendered by the caller.
   *
   * @param {Object} pMachineLogicLibrary
   * @param {Object} pMachineAppApplication The MachineAppApplication
   * @returns {BuildableUi}
   */
  createBuildableOperatorUi(pMachineLogicLibrary, pMachineAppApplication) {
    const lMachineAppType = pMachineAppApplication.getType();
    const lBuildOperatorUi = this._loadMachineAppImplementationModule(lMachineAppType).buildOperatorUi;

    const lAppBuilder = new ml_app_builder.MachineLogicAppBuilder(pMachineLogicLibrary);
    const lMachineAppLibrary = pMachineAppApplication.getLibrary();

    const lOperatorUiBuilderInterface = {
      machineAppInterface       : machineAppInterface.get(),
      machineLogicAppBuilder    : lAppBuilder,
      savedCalibration          : lMachineAppLibrary.getMachineParameters().getData(),
      savedConfigurations       : lMachineAppLibrary.listConfigurations().map(config => config.cloneConfiguration()),
      savedOperationData        : pMachineAppApplication.getAppData(),
      saveOperationData         : operationData => pMachineAppApplication.setAppData(operationData),

      // Deprecated, kept for compatibility until we review/remove the Cartesian Palletizer
      machineAppApplication     : pMachineAppApplication,
      machineLogicLibrary       : pMachineLogicLibrary,
      machineAppLibrary         : lMachineAppLibrary,
    };

    if (this.getGlobalAssetCreatorMap(lMachineAppType)) {
      lOperatorUiBuilderInterface.savedGlobalAssets = lMachineAppLibrary.listGlobalAssets().map(globalAsset => globalAsset.cloneGlobalAsset());
    }

    return lBuildOperatorUi(lOperatorUiBuilderInterface);
  }

  /**
   * Returns the MachineAppGenerator for the provided type.
   *
   * @param {Library} pMachineLogicLibrary The MachineLogicLibrary
   * @param {MachineAppApplication} pMachineAppApplication The MachineAppApplication
   * @param {string} pAppName The name of the application to generate
   *
   * @returns {Promise<BuildableApp | string[]>} A promise that resolves to the generated BuildableApp or rejects with a list of errors
   */
  buildApp(pMachineLogicLibrary, pMachineAppApplication, pAppName) {
    const lMachineAppType = pMachineAppApplication.getType();
    const lMachineAppGenerator = this._loadMachineAppImplementationModule(lMachineAppType).generator;

    const lMachineAppLibrary = pMachineAppApplication.getLibrary();
    const lAppBuilder = new ml_app_builder.MachineLogicAppBuilder(pMachineLogicLibrary);
    const lBuildableApp = lAppBuilder.createApp(pAppName);

    const lGeneratorInterface = {
      machineAppInterface       : machineAppInterface.get(),
      machineLogicAppBuilder    : lAppBuilder,
      buildableApp              : lBuildableApp,

      applyInjectedResources    : () => applyInjectedResources(lBuildableApp, pMachineAppApplication),
      resolveInjectionPoint     : (pInjectionPoint) => resolveInjectionPoint(pMachineAppApplication, pInjectionPoint),

      savedCalibration          : lMachineAppLibrary.getMachineParameters().getData(),
      savedConfigurations       : lMachineAppLibrary.listConfigurations().map(config => config.cloneConfiguration()),
      savedOperationData        : pMachineAppApplication.getAppData(),

      // Deprecated, kept for compatibility until we review/remove the Cartesian Palletizer
      machineAppApplication     : pMachineAppApplication,
      machineLogicLibrary       : pMachineLogicLibrary,
      machineAppLibrary         : lMachineAppLibrary,
    };

    if (this.getGlobalAssetCreatorMap(lMachineAppType)) {
      lGeneratorInterface.savedGlobalAssets = lMachineAppLibrary.listGlobalAssets().map(globalAsset => globalAsset.cloneGlobalAsset());
    }

    return lMachineAppGenerator(lGeneratorInterface);
  }

  /**
   * Returns the globalAssetCreatorMap for the provided type. It is expexted to return undefined
   * if the globalAssetCreator map is not defined for a gicen Machine App type.
   *
   * @param {string} pType Type of MachineApp prerequisites that you want to see
   * @returns {Record<string, (globalAssetInterface) => Jquery | ReactElement> || undefined}
   */
  getGlobalAssetCreatorMap(pType) {
    return this._loadMachineAppImplementationModule(pType).globalAssetCreatorMap;
  }

  /**
   * @typedef {Object} MachineAppValidator
   * @property {(machineParameters: any, machineAppInterface: any) => { success: boolean, errorList: string[] }} machineParameters
   * @property {(configuration: object) => { success: boolean, errorList: string[] }} configuration
   */

  /**
   * Returns the validator for the provided MachineApp type. If any validator is not
   * defined, the validation will always be successful.
   *
   * @param {string} pType Machineapp type
   * @returns {MachineAppValidator}
   */
  getValidators(pType) {
    const lDefaultValidators = {
      machineParameters: () => ({ success: true }),
      globalAssetValidatorMap: { 'default': () => ({ success: true }) },
      configuration: () => ({ success: true })
    };
    const lValidator = this._loadMachineAppImplementationModule(pType).validators;
    return Object.assign(lDefaultValidators, lValidator);
  }

  /**
   * @typedef {Object} OptionalUiHooks
   * @property {Partial<PreviewSpec<TConfigurationDataJson, TMachineParametersDataJson>>=} preview
   * @property {FlavorText=} flavorText
   * @property {() => JQuery=} getThumbnail
   * @property {() => EasyStepsData[]=} getEasySteps
   */

  /**
   * The 'optional ui' definitions provides an interface with tiny functions that affect
   * small pieces of the UI, such as the preview when you hover over an element. Each
   * value is allowed to be undefined.
   *
   * @param {String} pType MachineApp type
   * @returns {OptionalUiHooks} Interface for retrieving extras
   */
  getOptionalUiHooks(pType) {
    return this._loadMachineAppImplementationModule(pType).optionalUiHooks || {};
  }

  /**
   * Retrieves the flavor text for the provided MachineApp. If the app is missing any flavor text
   * it will fill in the defaults.
   *
   * @param {string} pType Type of the MachineApp
   * @returns {Object}
   */
  getFlavorText(pType) {
    const lOptionalUiHooks = this.getOptionalUiHooks(pType);
    const lImplementation = this.mImplementationManager.getMachineAppDefinitionByType(pType);
    const lAppName = lImplementation.getName();

    const lFlavorText = {
      general: {
        title: lAppName,
        description: 'Create a ' + lAppName + ' MachineApp'
      },
      learnMoreModal: {
        title: `How can the ${lAppName} MachineApp help me?`,
        description: 'Create a ' + lAppName + ' MachineApp',
        easyStepsHeader : `steps to generate your custom ${lAppName.toLowerCase()} application`
      },
      machineParameters: {
        singularName: 'Machine Parameter',
        pluralName: 'Machine Parameters',
        createButton: 'Create a machine parameter',
        description: 'This step allows you to map your machine configuration<br/>to your MachineApp.',
        defaultName: 'Machine Parameters'
      },
      globalAssetFlavorTextMap: {
        default: {
          singularName: 'Global Asset',
          pluralName: 'Global Assets',
          createButton: 'Create a new global asset',
          description: 'Create a unique global asset by clicking the button below',
          defaultName: 'Global Asset',
        }
      },
      configuration: {
        singularName: 'Configuration',
        pluralName: 'Configurations',
        createButton: 'Create a new configuration',
        description: 'Create a unique configuration by clicking the button below',
        defaultName: 'Configuration'
      },
    };

    Object.assign(lFlavorText.general, lOptionalUiHooks.flavorText?.general);
    Object.assign(lFlavorText.learnMoreModal, lOptionalUiHooks.flavorText?.learnMoreModal);
    Object.assign(lFlavorText.machineParameters, lOptionalUiHooks.flavorText?.machineParameters);
    Object.assign(lFlavorText.globalAssetFlavorTextMap, lOptionalUiHooks.flavorText?.globalAssetFlavorTextMap);
    Object.assign(lFlavorText.configuration, lOptionalUiHooks.flavorText?.configuration);

    if (lOptionalUiHooks.flavorText?.designTemplatesLink) {
      lFlavorText.designTemplatesLink = lOptionalUiHooks.flavorText.designTemplatesLink;
    }

    return lFlavorText;
  }

  /**
   * Retrieves the list of injectable points allowed for the MachineApp.
   * @param {string} pType
   * @returns {Object[]}
   */
  getInjectablePointList(pType) {
    return this._loadMachineAppImplementationModule(pType).getInjectableBehaviorPoints?.() || [];
  }

  /**
   * Retrieves versioning information about the MachineApp, including information on how
   * to upgrade from one version to the next. If this versioning is not defined, we default
   * to version 1.0 with a passthrough upgrade function.
   * @param {string} pType
   * @returns {Object}
   */
  getVersioning(pType) {
    const lDefaultVersion = { major: 1, minor: 0 };
    const lDefaultVersioning = {
      getVersionList: () => [ lDefaultVersion ],
      getCurrentVersion: () => lDefaultVersion,
      upgrade: (pVersion, pVersionList, pJson, pType) => pJson
    };

    return Object.assign(lDefaultVersioning, this._loadMachineAppImplementationModule(pType).versioning);
  }

  /**
   * Returns a function with which an implementer can render some admin-related functionality.
   * @param {string} pType
   * @returns {(props: Object) => void}
   */
  getAdminView(pType) {
    return this._loadMachineAppImplementationModule(pType).admin;
  }
}

module.exports = {
  instance: new MachineAppImplementationLoader()
};
